import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { travelAPI } from '../lib/api';
import { formatDate, formatCurrency } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Plane,
  Train,
  Car,
  Plus,
  Eye,
  Edit,
  XCircle,
  MapPin,
  Calendar,
  IndianRupee,
  Clock,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: Edit },
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-800', icon: Clock },
  l1_pending: { label: 'L1 Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  l1_approved: { label: 'L1 Approved', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  l1_rejected: { label: 'L1 Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
  l2_pending: { label: 'L2 Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  l2_approved: { label: 'L2 Approved', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  l2_rejected: { label: 'L2 Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
  l3_pending: { label: 'L3 Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  l3_approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  l3_rejected: { label: 'L3 Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-800', icon: XCircle },
  completed: { label: 'Completed', color: 'bg-purple-100 text-purple-800', icon: CheckCircle },
};

const travelTypeConfig = {
  local_domestic: { label: 'Local', color: 'bg-cyan-100 text-cyan-800' },
  domestic_outstation: { label: 'Domestic', color: 'bg-indigo-100 text-indigo-800' },
  international: { label: 'International', color: 'bg-orange-100 text-orange-800' },
};

const modeIcons = {
  flight: Plane,
  train: Train,
  bus: Car,
  cab: Car,
  self_drive: Car,
  other: MapPin,
};

export default function MyTravelRequestsPage() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await travelAPI.getAll(params);
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching travel requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this request?')) return;
    try {
      await travelAPI.cancel(id, 'Cancelled by employee');
      fetchRequests();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to cancel request');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope']">My Travel Requests</h1>
          <p className="text-muted-foreground">View and manage your travel requests</p>
        </div>
        <Button onClick={() => navigate('/travel/new')} className="gap-2">
          <Plus className="w-4 h-4" />
          New Request
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="l1_pending,l2_pending,l3_pending">Pending</SelectItem>
            <SelectItem value="l3_approved">Approved</SelectItem>
            <SelectItem value="l1_rejected,l2_rejected,l3_rejected">Rejected</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Requests List */}
      {requests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Plane className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No travel requests yet</h3>
            <p className="text-muted-foreground mb-4">Create your first travel request to get started</p>
            <Button onClick={() => navigate('/travel/new')}>
              <Plus className="w-4 h-4 mr-2" />
              New Request
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => {
            const status = statusConfig[request.status] || statusConfig.draft;
            const StatusIcon = status.icon;
            const travelType = travelTypeConfig[request.travel_type] || travelTypeConfig.local_domestic;
            const firstLeg = request.itinerary?.[0];
            const lastLeg = request.itinerary?.[request.itinerary.length - 1];
            const ModeIcon = modeIcons[firstLeg?.mode_of_travel] || MapPin;

            return (
              <Card key={request.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    {/* Left: Trip Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono text-sm text-muted-foreground">
                          {request.request_number}
                        </span>
                        <Badge className={travelType.color}>{travelType.label}</Badge>
                        <Badge className={status.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {status.label}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-2 text-lg font-semibold mb-2">
                        <ModeIcon className="w-5 h-5 text-primary" />
                        <span>{firstLeg?.from_location}</span>
                        <span className="text-muted-foreground">→</span>
                        <span>{lastLeg?.to_location || firstLeg?.to_location}</span>
                      </div>

                      <p className="text-muted-foreground text-sm mb-3 line-clamp-1">
                        {request.purpose}
                      </p>

                      <div className="flex items-center gap-6 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {formatDate(firstLeg?.departure_date)}
                            {firstLeg?.return_date && ` - ${formatDate(firstLeg.return_date)}`}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <IndianRupee className="w-4 h-4" />
                          <span>{formatCurrency(request.estimated_costs?.total_estimated || 0)}</span>
                        </div>
                        {request.advance_request?.required && (
                          <Badge variant="outline" className="text-xs">
                            Advance: {formatCurrency(request.advance_request.amount)}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/travel/${request.id}`)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      {(request.status === 'draft' || request.status.includes('rejected')) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/travel/${request.id}/edit`)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      )}
                      {!['cancelled', 'completed', 'l3_approved'].includes(request.status) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleCancel(request.id)}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Approval Progress */}
                  {request.status !== 'draft' && request.status !== 'cancelled' && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center gap-4">
                        <ApprovalStep
                          label="L1"
                          name={request.approval_workflow?.l1_approver_name || 'Manager'}
                          status={getApprovalStepStatus(request, 'l1')}
                        />
                        <div className="h-px w-8 bg-gray-200" />
                        <ApprovalStep
                          label="L2"
                          name={request.approval_workflow?.l2_approver_name || 'Dept Head'}
                          status={getApprovalStepStatus(request, 'l2')}
                        />
                        <div className="h-px w-8 bg-gray-200" />
                        <ApprovalStep
                          label="L3"
                          name={request.approval_workflow?.l3_approver_name || 'Finance'}
                          status={getApprovalStepStatus(request, 'l3')}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ApprovalStep({ label, name, status }) {
  const config = {
    pending: { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-50' },
    approved: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50' },
    rejected: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50' },
    waiting: { icon: Clock, color: 'text-gray-300', bg: 'bg-gray-50' },
  };

  const { icon: Icon, color, bg } = config[status] || config.waiting;

  return (
    <div className="flex items-center gap-2">
      <div className={`p-1.5 rounded-full ${bg}`}>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <div>
        <p className="text-xs font-medium">{label}</p>
        <p className="text-xs text-muted-foreground truncate max-w-[80px]">{name}</p>
      </div>
    </div>
  );
}

function getApprovalStepStatus(request, level) {
  const status = request.status;
  
  if (status === `${level}_pending`) return 'pending';
  if (status === `${level}_rejected`) return 'rejected';
  if (status === `${level}_approved` || 
      (level === 'l1' && ['l2_pending', 'l2_approved', 'l3_pending', 'l3_approved'].includes(status)) ||
      (level === 'l2' && ['l3_pending', 'l3_approved'].includes(status)) ||
      (level === 'l3' && status === 'l3_approved')) {
    return 'approved';
  }
  return 'waiting';
}
